import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { parseCallSign, parseTime } from '../utils/dataUtils';
import CreateLogComponent from './CreateLog';
import { ChevronDown, Plus } from './icons';
import type { MessageLog } from '@/data/logs';
import { $currentOperator, $defaultFrequency, $myCallsign } from '@/stores';
import { logs } from '@/stores/logbook';
import { alert } from '@/utils/alertUtils';
import confetti from '@hiseb/confetti';
import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function NewLogComponent() {
  const operator = useStore($currentOperator);
  const myCallsign = useStore($myCallsign);
  const defaultFrequency = useStore($defaultFrequency);

  const callSignRef = useRef<HTMLInputElement>(null);
  const [frequency, setFrequency] = useState('');
  const [callSign, setCallSign] = useState('');
  const [description, setDescription] = useState('');
  const [time, setTime] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);

  const save = useCallback(() => {
    if (description === '') {
      return;
    }

    const newTime = parseTime(time);

    if (newTime === null) {
      alert({
        title: 'Error',
        message: 'Invalid time format',
      });
      return;
    }

    const parsedCallSign = parseCallSign(callSign, myCallsign);
    if (!parsedCallSign) {
      alert({
        title: 'Error',
        message: 'Invalid call sign format',
      });
      return;
    }

    const log: MessageLog = {
      id: `log-${Date.now()}`,
      type: 'message',
      draft: false,
      tags: tagIds,
      entries: [
        {
          timestamp: Date.now(),
          frequency: frequency || defaultFrequency,
          callSign: parsedCallSign,
          time: newTime,
          description,
          operator,
        },
      ],
    };

    logs.addLog(log);

    setFrequency('');
    setCallSign('');
    setDescription('');
    setTime('');
    setTagIds([]);

    if (/\bendex\b/i.test(description)) {
      // Trigger confetti animation for "endex" keyword
      confetti({
        count: 200,
        position: { x: window.innerWidth / 2, y: -100 },
      });
    }

    requestAnimationFrame(() => {
      callSignRef.current?.focus();
    });
  }, [
    description,
    time,
    callSign,
    myCallsign,
    tagIds,
    frequency,
    defaultFrequency,
    operator,
  ]);

  const saveDraft = useCallback(() => {
    if (description === '') {
      return;
    }

    const newTime = parseTime(time, true);

    if (newTime === null) {
      alert({
        title: 'Error',
        message: 'Invalid time format',
      });
      return;
    }

    const parsedCallSign = parseCallSign(callSign, myCallsign);
    if (!parsedCallSign) {
      alert({
        title: 'Error',
        message: 'Invalid call sign format',
      });
      return;
    }

    const log: MessageLog = {
      id: `log-${Date.now()}`,
      type: 'message',
      draft: true,
      tags: tagIds,
      entries: [
        {
          timestamp: Date.now(),
          frequency: frequency || defaultFrequency,
          callSign: parsedCallSign,
          time: newTime,
          description,
          operator,
        },
      ],
    };

    logs.addLog(log);

    setFrequency('');
    setCallSign('');
    setDescription('');
    setTime('');
    setTagIds([]);

    requestAnimationFrame(() => {
      callSignRef.current?.focus();
    });
  }, [
    callSign,
    description,
    frequency,
    time,
    defaultFrequency,
    myCallsign,
    operator,
    tagIds,
  ]);

  useEffect(() => {
    // Focus the callsign input on mount
    callSignRef.current?.focus();
  }, []);

  return (
    <CreateLogComponent
      id="new-log"
      className="bg-yellow-100"
      callSignRef={callSignRef}
      frequency={frequency}
      setFrequency={setFrequency}
      callSign={callSign}
      setCallSign={setCallSign}
      description={description}
      setDescription={setDescription}
      time={time}
      setTime={setTime}
      tagIds={tagIds}
      setTagIds={setTagIds}
      save={save}
      saveButton={
        <div className="flex h-full items-center justify-center">
          <button
            id="new-log-save"
            onClick={save}
            className="h-full cursor-pointer rounded-l border-r-2 border-r-green-600 bg-green-500 py-2 pr-1 pl-2 text-white hover:bg-green-600"
          >
            <Plus className="h-4 w-4" />
          </button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                id="new-log-save-dropdown"
                className="cursor-pointer rounded-r bg-green-500 py-2 text-white hover:bg-green-600"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              className="dropdown-menu absolute right-0 w-40 rounded border border-gray-300 bg-white text-black shadow-lg"
              sideOffset={5}
              alignOffset={0}
              align="end"
            >
              <DropdownMenu.Item
                className="block w-full cursor-pointer px-4 py-2 text-left hover:bg-gray-100"
                onSelect={saveDraft}
              >
                Add Draft
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="block w-full cursor-pointer px-4 py-2 text-left hover:bg-gray-100"
                onSelect={save}
              >
                Add Log
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>
      }
    />
  );
}
